import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormField,
  FormItem,
  Icon,
  Stack,
  Textarea,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

const appointmentDetailsChiefComplaintReasonForVisitFragment = graphql(`
  fragment AppointmentDetailsChiefComplaintReasonForVisit on Appointments
  @_unmask {
    __typename
    id
    chiefComplaint
    patientReasonForVisit
  }
`);

export const AppointmentDetailsChiefComplaintReasonForVisit = ({
  appointmentId,
  tenantId,
}: {
  appointmentId: string;
  tenantId: string;
}) => {
  const data = useCompleteFragment({
    fragment: appointmentDetailsChiefComplaintReasonForVisitFragment,
    key: {
      id: appointmentId,
    },
  });
  const toast = useToast();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateAppointmentDetailsMutation = graphql(
    `
      mutation UpdateAppointmentChiefComplaint(
        $tenantId: uuid!
        $id: uuid!
        $input: AppointmentsSetInput!
      ) {
        updateAppointmentsByPk(
          pkColumns: { tenantId: $tenantId, id: $id }
          _set: $input
        ) {
          ...AppointmentDetailsChiefComplaintReasonForVisit
        }
      }
    `,
    [appointmentDetailsChiefComplaintReasonForVisitFragment],
  );

  const [updateAppointmentDetails] = useMutation(
    updateAppointmentDetailsMutation,
    {
      optimisticResponse: ({ input, id }) => {
        return {
          updateAppointmentsByPk: {
            __typename: 'Appointments' as const,
            id,
            chiefComplaint: input.chiefComplaint ?? data.chiefComplaint,
            patientReasonForVisit:
              input.patientReasonForVisit ?? data.patientReasonForVisit,
          },
        };
      },
      onError: () => {
        toast.error('The appointment details could not be updated.');
      },
    },
  );

  const form = useForm({
    values: {
      chiefComplaint: data.chiefComplaint ?? '',
      patientReasonForVisit: data.patientReasonForVisit ?? '',
    },
  });

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const values = form.getValues();
      await updateAppointmentDetails({
        variables: {
          tenantId,
          id: appointmentId,
          input: {
            chiefComplaint: values.chiefComplaint || null,
            patientReasonForVisit: values.patientReasonForVisit || null,
          },
        },
      });
      setIsDialogOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" fullWidth>
          <Icon name="Plus" size="auto" />
          {data.chiefComplaint || data.patientReasonForVisit
            ? 'Update Context'
            : 'Add Context'}
        </Button>
      </DialogTrigger>

      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Chief Complaint & Reason For Visit</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <Stack gap={2}>
            <span className="text-sm font-normal text-text-secondary">
              Chief Complaint
            </span>
            <FormField
              control={form.control}
              name="chiefComplaint"
              render={({ field }) => (
                <FormItem className="w-full">
                  <Textarea
                    {...field}
                    placeholder="Enter chief complaint"
                    className="min-h-[80px] w-full resize-none"
                  />
                </FormItem>
              )}
            />

            <span className="text-sm font-normal text-text-secondary">
              Reason For Visit
            </span>
            <FormField
              control={form.control}
              name="patientReasonForVisit"
              render={({ field }) => (
                <FormItem className="w-full">
                  <Textarea
                    {...field}
                    placeholder="Enter reason for visit"
                    className="min-h-[80px] w-full resize-none"
                  />
                </FormItem>
              )}
            />
          </Stack>
        </Form>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isSubmitting}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
